import { useCallback, useMemo } from 'react';

import Fuse from 'fuse.js';
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';
import { graphql } from 'relay-runtime';

import {
	useSearchSuggestionsContentfulQuery,
	useSearchSuggestionsContentfulQuery$data,
} from '@/__generated_contentful__/useSearchSuggestionsContentfulQuery.graphql';

import { CategorySearchSuggestion } from '../../components/contentful-relay-components/template-category-search-results-contentful-relay';
import { TemplateSearchSuggestion } from '../../components/contentful-relay-components/template-fuzzy-search-results-contentful-relay';
import { tranformCategories } from '../../contentful-relay/confluence-templates-unified-contentful-relay-page.template';
import { useQueryLocale } from '../../hooks/use-query-locale';

export type SearchSuggestion = CategorySearchSuggestion | TemplateSearchSuggestion;

export const FUSE_OPTIONS = {
	keys: [
		{ name: 'name', weight: 0.8 }, // Increased weight for better matches on name
		{ name: 'description', weight: 0.5 },
		{ name: 'contributor', weight: 0.3 },
		{ name: 'contributorOrganization', weight: 0.3 },
		{ name: 'customTag', weight: 0.3 },
	],
	includeScore: true,
	threshold: 0.4,
};

const FUSE_RESULT_LIMIT = 35;

const SLUG_TO_TAGS: Record<string, string[]> = {
	'project-plan': ['proposal', 'gantt', 'timeline'],
	'master-project-documentation': ['proposal'],
	'annual-plan-one-pager': ['proposal'],
	'mutual-action-plan': ['proposal'],
	'executive-business-review': ['proposal'],
	'devops-runbook': ['gantt', 'timeline'],
	'software-architecture-review': ['gantt'],
	'problem-framing': ['faq'],
	'safe-lean-business-case': ['faq'],
	'hr-knowledge-base': ['faq'],
	'design-sprint': ['timeline'],
	'project-kickoff': ['timeline'],
	'year-in-review': ['timeline'],
};

export const WHITEBOARD_TYPE_ID = '1HlskYfRNciSnQ02SDhCPs';

const getAuthor = (
	template: NonNullable<
		NonNullable<useSearchSuggestionsContentfulQuery$data['productTemplateCollection']>['items'][0]
	>,
) => {
	return template.contributor?.__typename === 'Author' ? template.contributor : null;
};

const getOrganization = (
	template: NonNullable<
		NonNullable<useSearchSuggestionsContentfulQuery$data['productTemplateCollection']>['items'][0]
	>,
) => {
	return template.contributor?.__typename === 'CompanyNameInternal'
		? template.contributor
		: template.contributor?.__typename === 'Author'
			? template.contributor?.organization
			: null;
};

const addCustomTagToTemplatesBasedOnSearchTerm = (
	template: NonNullable<
		NonNullable<useSearchSuggestionsContentfulQuery$data['productTemplateCollection']>['items'][0]
	>,
) => {
	const tags = (template.urlSlug ? SLUG_TO_TAGS[template.urlSlug] : []) ?? [];

	/**
	 * Whiteboard templates for custom tag search term "whiteboard templates"
	 */
	if (template.templateType?.items?.some((type) => type?.sys?.id === WHITEBOARD_TYPE_ID)) {
		tags.push('whiteboard templates');
	}

	return tags;
};

export const parseTemplateSearchItems = (
	templates:
		| NonNullable<useSearchSuggestionsContentfulQuery$data['productTemplateCollection']>
		| null
		| undefined,
) => {
	// filter out any templates that are not Confluence
	templates = {
		items:
			templates?.items?.filter(
				(template) =>
					template?.productCollection?.items[0]?.productNameReference?.productNameId ===
					'confluence',
			) || [],
	};
	const items: TemplateSearchSuggestion[] = [];
	if (!templates || !templates.items) {
		return items;
	}
	for (const template of templates.items) {
		if (template?.urlSlug) {
			items.push({
				id: template.urlSlug,
				name: template.name,
				description: template.previewBlurb,
				contributor: getAuthor(template)?.name,
				contributorOrganization: getOrganization(template)?.name,
				customTag: addCustomTagToTemplatesBasedOnSearchTerm(template),
				type: 'template',
				templateType: template.templateType?.items?.[0]?.templateTypeName,
			});
		}
	}
	return items;
};

export const parseCategorySearchItems = (
	categories: useSearchSuggestionsContentfulQuery$data['categories'] | null | undefined,
) => {
	const transformedCategories = tranformCategories(categories);
	const items: CategorySearchSuggestion[] = [];
	for (const { node } of transformedCategories.edges) {
		if (
			node.context.urlSlug !== 'featured' &&
			node.context.urlSlug !== 'recommended' &&
			node.context.urlSlug !== 'project-planning' &&
			node.context.urlSlug !== 'business-strategy' &&
			node.context.urlSlug !== 'productivity' &&
			node.context.urlSlug !== 'docs-and-reports' &&
			node.context.urlSlug !== 'startup' &&
			node.context.urlSlug !== 'personal'
		) {
			items.push({
				id: node.context.urlSlug,
				name: node.context.name,
				description: node.context.shortDescriptionOneLiner,
				type: 'category',
			});
		}
	}
	return items;
};

export const UseSearchSuggestionsQuery = graphql`
	query useSearchSuggestionsContentfulQuery($locale: String!) {
		productTemplateCollection(limit: 400, locale: $locale) {
			items {
				productCollection(limit: 1) {
					items {
						productNameReference {
							productNameId
						}
					}
				}
				urlSlug
				name: templateNameExternal
				previewBlurb
				templateType: templateTypeCollection {
					items {
						sys {
							id
						}
						templateTypeName: templateTypeNameExternal
					}
				}
				contributor {
					__typename
					... on CompanyNameInternal {
						name: organizationNameExternal
					}
					... on Author {
						name: authorNameExternal
						organization {
							name: organizationNameExternal
						}
					}
				}
			}
		}
		categories: productTemplateCollection(limit: 400, locale: $locale) {
			items {
				categoryCollection(limit: 10) {
					total
					items {
						sys {
							id
						}
						urlSlug
						categoryNameInternal
						categoryNameExternal
						shortDescriptionOneLiner
						experiment
						linkedFrom(
							allowedLocales: [
								"en-US"
								"es"
								"ja-JP"
								"pt-BR"
								"fr-FR"
								"de-DE"
								"nl-NL"
								"it-IT"
								"ko-KR"
								"pl-PL"
								"ru-RU"
								"zh-CN"
							]
						) {
							productTemplateCollection(limit: 1) {
								total
							}
						}
					}
				}
			}
		}
	}
`;

export const useSearchSuggestionsLoader = () => {
	const locale = useQueryLocale();
	const [queryRef, loadQuery] =
		useQueryLoader<useSearchSuggestionsContentfulQuery>(UseSearchSuggestionsQuery);
	const loadSearchSuggestionsQuery = useCallback(() => {
		loadQuery({ locale });
	}, [loadQuery, locale]);
	return [queryRef, loadSearchSuggestionsQuery] as const;
};

export const useSearchSuggestions = (
	queryRef: PreloadedQuery<useSearchSuggestionsContentfulQuery>,
	inputValue?: string | SearchSuggestion | null,
) => {
	const { categories, productTemplateCollection } =
		usePreloadedQuery<useSearchSuggestionsContentfulQuery>(UseSearchSuggestionsQuery, queryRef);

	const fuse = useMemo(
		() =>
			new Fuse(
				[
					...parseTemplateSearchItems(productTemplateCollection),
					...parseCategorySearchItems(categories),
				],
				FUSE_OPTIONS,
			),
		[productTemplateCollection, categories],
	);

	return useMemo(() => {
		let results: SearchSuggestion[] = [];

		// remove duplicates, but keep best (lowest) score
		if (inputValue) {
			// Create a Map to track items and their scores
			const itemMap = new Map<string, { item: SearchSuggestion; score: number }>();

			fuse
				.search(typeof inputValue === 'string' ? inputValue : (inputValue?.name ?? ''))
				.forEach((result) => {
					const existing = itemMap.get(result.item.id);
					// Keep the item with the better (lower) score
					if (!existing || (result.score ?? Infinity) < existing.score) {
						itemMap.set(result.item.id, {
							item: result.item,
							score: result.score ?? Infinity,
						});
					}
				});

			// Sort by score and convert to array
			results = Array.from(itemMap.values())
				.sort((a, b) => a.score - b.score)
				.slice(0, FUSE_RESULT_LIMIT)
				.map(({ item }) => item);
		}

		return results;
	}, [fuse, inputValue]);
};
